/**
 * Global API Client with resilience features:
 * - Timeout handling (explicit)
 * - Retry only for idempotent GET (max 1-2)
 * - On 401: single redirect to login, no infinite-loop
 * - Request deduplication for auth/me
 */

const API_BASE = `${window.location.origin || ''}/api`;

// In-flight request tracking for deduplication
const inFlightRequests = new Map();

// Auth state indicator for debugging
let authState = { loggedIn: false, lastCheck: null };

// In-memory auth token (NEVER persisted to localStorage; httpOnly cookie is the
// primary credential, the in-memory token is a fallback for legacy code paths
// that still send `Authorization: Bearer ...`)
let inMemoryAuthToken = null;

/**
 * Set the in-memory auth token (called by AuthContext on login/logout)
 */
export const setAuthToken = (token) => {
  inMemoryAuthToken = token || null;
};

/**
 * Read the in-memory auth token (used by legacy admin components)
 */
export const getAuthToken = () => inMemoryAuthToken;

/**
 * Read the CSRF token from the non-httpOnly cookie set by the backend on login.
 * Returns empty string if not present (e.g., before login or after logout).
 */
export const getCsrfToken = () => {
  if (typeof document === 'undefined' || !document.cookie) return '';
  const match = document.cookie.match(/(?:^|; )csrf_token=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : '';
};

const STATE_CHANGING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Build CSRF header object for state-changing requests.
 * Returns { 'X-CSRF-Token': '...' } or {} when not applicable.
 */
export const csrfHeader = (method) => {
  if (!method || !STATE_CHANGING_METHODS.has(method.toUpperCase())) return {};
  const token = getCsrfToken();
  return token ? { 'X-CSRF-Token': token } : {};
};

// Singleton redirect guard to prevent infinite 401 loops
let isRedirectingToLogin = false;

/**
 * Get current auth state for debugging
 */
export const getAuthState = () => ({ ...authState });

/**
 * Update auth state
 */
export const setAuthState = (loggedIn) => {
  authState = { loggedIn, lastCheck: new Date().toISOString() };
};

/**
 * Reset redirect guard (call after successful login)
 */
export const resetRedirectGuard = () => {
  isRedirectingToLogin = false;
};

/**
 * Create a timeout promise
 */
const createTimeout = (ms) => {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(`Request timeout after ${ms}ms`)), ms);
  });
};

/**
 * Main fetch wrapper with resilience
 */
export const apiFetch = async (endpoint, options = {}) => {
  const {
    timeout = 30000,
    retries = 0,
    dedupe = false,
    skipAuthRedirect = false,
    ...fetchOptions
  } = options;

  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;
  const method = (fetchOptions.method || 'GET').toUpperCase();
  
  // Deduplication for GET requests (especially auth/me)
  if (dedupe && method === 'GET') {
    const dedupeKey = url;
    if (inFlightRequests.has(dedupeKey)) {
      return inFlightRequests.get(dedupeKey);
    }
    
    const requestPromise = executeRequest(url, fetchOptions, timeout, retries, method, skipAuthRedirect);
    inFlightRequests.set(dedupeKey, requestPromise);
    
    try {
      const result = await requestPromise;
      return result;
    } finally {
      inFlightRequests.delete(dedupeKey);
    }
  }

  return executeRequest(url, fetchOptions, timeout, retries, method, skipAuthRedirect);
};

/**
 * Execute the actual request with timeout and retry
 */
const executeRequest = async (url, fetchOptions, timeout, retries, method, skipAuthRedirect) => {
  let lastError;
  const maxAttempts = method === 'GET' ? retries + 1 : 1; // Only retry GETs
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const response = await Promise.race([
        fetch(url, {
          ...fetchOptions,
          credentials: 'include',
          headers: {
            ...(fetchOptions.headers || {}),
            ...csrfHeader(method),
          },
        }),
        createTimeout(timeout)
      ]);

      // Handle 401 - single redirect, no loop
      if (response.status === 401 && !skipAuthRedirect) {
        setAuthState(false);
        
        // Only redirect once
        if (!isRedirectingToLogin && !url.includes('/auth/me')) {
          isRedirectingToLogin = true;
          // Use a small delay to allow current operations to complete
          setTimeout(() => {
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
            // Reset after navigation attempt
            setTimeout(() => { isRedirectingToLogin = false; }, 2000);
          }, 100);
        }
        
        // Return a proper error response
        return {
          ok: false,
          status: 401,
          json: async () => ({ detail: 'Unauthorized' }),
          text: async () => 'Unauthorized'
        };
      }

      return response;
      
    } catch (error) {
      lastError = error;
      
      // Don't retry if it's not a network/timeout error
      if (!error.message.includes('timeout') && !error.message.includes('network')) {
        break;
      }
      
      // Wait before retry
      if (attempt < maxAttempts) {
        await new Promise(r => setTimeout(r, 500 * attempt));
      }
    }
  }

  // All retries failed
  throw lastError;
};

/**
 * Convenience methods
 */
export const apiGet = (endpoint, options = {}) => 
  apiFetch(endpoint, { ...options, method: 'GET' });

export const apiPost = (endpoint, body, options = {}) => 
  apiFetch(endpoint, { 
    ...options, 
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    body: JSON.stringify(body)
  });

export const apiPut = (endpoint, body, options = {}) => 
  apiFetch(endpoint, { 
    ...options, 
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    body: JSON.stringify(body)
  });

export const apiDelete = (endpoint, options = {}) => 
  apiFetch(endpoint, { ...options, method: 'DELETE' });

export default apiFetch;
